// components/designer/ExportManager.tsx
/**
 * Export Manager Hook
 * 
 * Handles exporting templates to PDF, JPG, and PNG formats.
 * Supports single and double-sided templates.
 */

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useState } from "react";
import { toast } from "@shared/hooks/use-toast";

interface ExportManagerOptions {
  canvasRef: React.RefObject<HTMLDivElement>;
  activeSide: "front" | "back";
  isDoubleSided: boolean;
  templateName: string;
  pageSize: { width: number; height: number };
  onSideChange: (side: "front" | "back") => void;
}

export function useExportManager({
  canvasRef,
  activeSide,
  isDoubleSided,
  templateName,
  pageSize,
  onSideChange,
}: ExportManagerOptions) {
  const [isExporting, setIsExporting] = useState(false);

  const captureCanvas = async (side: "front" | "back"): Promise<HTMLCanvasElement> => {
    const canvas = canvasRef.current;
    if (!canvas) throw new Error("Canvas not found");
    
    return html2canvas(canvas, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
  };

  const exportToPDF = async () => {
    try {
      setIsExporting(true);

      const frontCanvas = await captureCanvas("front");
      let backCanvas: HTMLCanvasElement | null = null;

      if (isDoubleSided) {
        const originalSide = activeSide;
        onSideChange("back");
        await new Promise((resolve) => setTimeout(resolve, 100));
        backCanvas = await captureCanvas("back");
        onSideChange(originalSide);
      }

      const pdf = new jsPDF({
        orientation: pageSize.width > pageSize.height ? "landscape" : "portrait",
        unit: "px",
        format: [pageSize.width, pageSize.height],
      });

      pdf.addImage(
        frontCanvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        pageSize.width,
        pageSize.height
      );

      if (backCanvas) {
        pdf.addPage();
        pdf.addImage(
          backCanvas.toDataURL("image/png"),
          "PNG",
          0,
          0,
          pageSize.width,
          pageSize.height
        );
      }

      pdf.save(`${templateName || "template"}.pdf`);

      toast({
        title: "Exported as PDF",
        description: "Your template has been downloaded",
      });
    } catch (error) {
      console.error("PDF export error:", error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your template",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToImage = async (format: "jpg" | "png") => {
    try {
      setIsExporting(true);

      const frontCanvas = await captureCanvas("front");
      let backCanvas: HTMLCanvasElement | null = null;

      if (isDoubleSided) {
        const originalSide = activeSide;
        onSideChange("back");
        await new Promise((resolve) => setTimeout(resolve, 100));
        backCanvas = await captureCanvas("back");
        onSideChange(originalSide);
      }

      const downloadImage = (canvas: HTMLCanvasElement, suffix: string) => {
        const link = document.createElement("a");
        link.download = `${templateName || "template"}${suffix}.${format}`;
        link.href = canvas.toDataURL(`image/${format}`);
        link.click();
      };

      downloadImage(frontCanvas, isDoubleSided ? "_front" : "");
      if (backCanvas) {
        downloadImage(backCanvas, "_back");
      }

      toast({
        title: `Exported as ${format.toUpperCase()}`,
        description: "Your template has been downloaded",
      });
    } catch (error) {
      console.error("Image export error:", error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your template",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToJPG = () => exportToImage("jpg");
  const exportToPNG = () => exportToImage("png");

  return {
    exportToPDF,
    exportToJPG,
    exportToPNG,
    isExporting,
  };
}
